import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Save, Plus, Trash2, Shield, Zap, Flame, Droplets, Trash,
  AlertTriangle, CheckCircle, Clock, ChevronRight, Check, X, Info,
  Gauge, Activity, BarChart3, Thermometer, ClipboardCheck, Eye, FileText,
  Lightbulb, Lock, MapPin, Camera, Upload, Settings
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';
import { DatePicker } from '../../components/ui/date-picker';
import { formatDateTime } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

// ==================== PHASE CONFIGURATION ====================

const PHASES = [
  { 
    id: 'safety', 
    label: 'Safety & Permit', 
    shortLabel: 'Safety',
    icon: Shield, 
    color: 'red',
    description: 'Complete safety checklist before proceeding',
    isGate: true
  },
  { 
    id: 'seu-selection', 
    label: 'SEU Selection', 
    shortLabel: 'SEUs',
    icon: MapPin, 
    color: 'blue',
    description: 'Select equipment to measure'
  },
  { 
    id: 'electrical', 
    label: 'Electrical', 
    shortLabel: 'Electrical',
    icon: Zap, 
    color: 'yellow',
    description: 'Power, voltage, current readings'
  },
  { 
    id: 'thermal', 
    label: 'Thermal / Fuel', 
    shortLabel: 'Thermal',
    icon: Flame, 
    color: 'orange',
    description: 'Combustion & steam measurements'
  },
  { 
    id: 'water-waste', 
    label: 'Water & Waste', 
    shortLabel: 'Water',
    icon: Droplets, 
    color: 'cyan',
    description: 'Water use & waste observations'
  },
  { 
    id: 'observations', 
    label: 'Observations', 
    shortLabel: 'Observe',
    icon: Eye, 
    color: 'purple',
    description: 'Equipment condition notes'
  },
  { 
    id: 'verification', 
    label: 'Verification', 
    shortLabel: 'Verify',
    icon: ClipboardCheck, 
    color: 'green',
    description: 'Verification checklist'
  },
  { 
    id: 'insights', 
    label: 'AI Insights', 
    shortLabel: 'Insights',
    icon: Lightbulb, 
    color: 'indigo',
    description: 'Auto-generated opportunities'
  },
  { 
    id: 'summary', 
    label: 'Summary & Close', 
    shortLabel: 'Summary',
    icon: FileText, 
    color: 'gray',
    description: 'Review and lock session'
  }
];

const DEFAULT_SAFETY_CHECKLIST = [
  { item: "Site safety induction done", checked: false, remarks: "", mandatory: true },
  { item: "PTW obtained", checked: false, remarks: "", mandatory: true },
  { item: "Electrical gloves used", checked: false, remarks: "", mandatory: false },
  { item: "Arc flash risk explained", checked: false, remarks: "", mandatory: false },
  { item: "Confined space entry required", checked: false, remarks: "", mandatory: false },
  { item: "Photos permitted", checked: false, remarks: "", mandatory: false },
  { item: "Lockout/Tagout procedures followed", checked: false, remarks: "", mandatory: false },
  { item: "PPE requirements met", checked: false, remarks: "", mandatory: false }
];

const SHIFTS = ['Day', 'Night', 'General'];
const OPERATING_CONDITIONS = ['Normal', 'Peak', 'Idle', 'Startup', 'Shutdown'];
const WEATHER_OPTIONS = ['Hot', 'Cold', 'Humid', 'Rainy', 'Normal'];
const INSTRUMENTS = ['Fluke 435', 'Fluke 87V', 'Hioki PW3198', 'Clamp Meter', 'Power Analyzer', 'Other'];
const OPERATING_STATES = ['Load', 'Idle', 'Unload', 'Off'];

// ==================== PHASE ICON COMPONENT ====================

function PhaseIcon({ phase, isActive, isUnlocked, isCompleted, onClick, index }) {
  const Icon = phase.icon;
  
  const getColorClasses = () => {
    if (isActive) {
      return {
        bg: `bg-${phase.color}-500`,
        text: 'text-white',
        border: `border-${phase.color}-500`,
        ring: `ring-${phase.color}-200`
      };
    }
    if (isCompleted) {
      return {
        bg: 'bg-green-500',
        text: 'text-white',
        border: 'border-green-500',
        ring: ''
      };
    }
    if (isUnlocked) {
      return {
        bg: 'bg-white',
        text: `text-${phase.color}-600`,
        border: `border-${phase.color}-300`,
        ring: ''
      };
    }
    return {
      bg: 'bg-gray-100',
      text: 'text-gray-400',
      border: 'border-gray-200',
      ring: ''
    };
  };
  
  const colors = getColorClasses();
  
  // Inline style for dynamic colors (Tailwind JIT doesn't handle dynamic class names well)
  const getInlineStyles = () => {
    const colorMap = {
      red: { bg: '#ef4444', light: '#fef2f2', border: '#fecaca', text: '#dc2626' },
      blue: { bg: '#3b82f6', light: '#eff6ff', border: '#bfdbfe', text: '#2563eb' },
      yellow: { bg: '#eab308', light: '#fefce8', border: '#fef08a', text: '#ca8a04' },
      orange: { bg: '#f97316', light: '#fff7ed', border: '#fed7aa', text: '#ea580c' },
      cyan: { bg: '#06b6d4', light: '#ecfeff', border: '#a5f3fc', text: '#0891b2' },
      purple: { bg: '#a855f7', light: '#faf5ff', border: '#e9d5ff', text: '#9333ea' },
      green: { bg: '#22c55e', light: '#f0fdf4', border: '#bbf7d0', text: '#16a34a' },
      indigo: { bg: '#6366f1', light: '#eef2ff', border: '#c7d2fe', text: '#4f46e5' },
      gray: { bg: '#6b7280', light: '#f9fafb', border: '#e5e7eb', text: '#4b5563' }
    };
    
    const c = colorMap[phase.color] || colorMap.gray;
    
    if (isActive) {
      return { backgroundColor: c.bg, color: 'white', borderColor: c.bg };
    }
    if (isCompleted) {
      return { backgroundColor: '#22c55e', color: 'white', borderColor: '#22c55e' };
    }
    if (isUnlocked) {
      return { backgroundColor: 'white', color: c.text, borderColor: c.border };
    }
    return { backgroundColor: '#f3f4f6', color: '#9ca3af', borderColor: '#e5e7eb' };
  };
  
  return (
    <div className="flex flex-col items-center relative">
      {/* Connector line */}
      {index > 0 && (
        <div 
          className={`absolute top-5 -left-1/2 w-full h-0.5 ${
            isUnlocked || isCompleted || isActive ? 'bg-green-300' : 'bg-gray-200'
          }`}
          style={{ zIndex: 0 }}
        />
      )}
      
      <button
        onClick={() => isUnlocked && onClick(phase.id)}
        disabled={!isUnlocked}
        className={`relative z-10 w-10 h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center transition-all ${
          isUnlocked ? 'cursor-pointer hover:scale-110' : 'cursor-not-allowed'
        } ${isActive ? 'ring-4' : ''}`}
        style={{
          ...getInlineStyles(),
          boxShadow: isActive ? `0 0 0 4px ${isActive ? 'rgba(59, 130, 246, 0.2)' : 'transparent'}` : 'none'
        }}
        title={phase.label}
      >
        {isCompleted && !isActive ? (
          <Check className="w-5 h-5" />
        ) : (
          <Icon className="w-5 h-5" />
        )}
        {!isUnlocked && (
          <div className="absolute -bottom-1 -right-1 bg-gray-400 rounded-full p-0.5">
            <Lock className="w-2.5 h-2.5 text-white" />
          </div>
        )}
      </button>
      
      <span className={`mt-1.5 text-xs font-medium text-center ${
        isActive ? 'text-gray-900' : isUnlocked ? 'text-gray-600' : 'text-gray-400'
      }`}>
        {phase.shortLabel}
      </span>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================

export default function OnSiteModule() {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, confirm } = useAlert();

  // Main state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [study, setStudy] = useState(null);
  const [site, setSite] = useState(null);
  
  // Session state
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [showNewSession, setShowNewSession] = useState(false);
  
  // Phase state
  const [activePhase, setActivePhase] = useState('safety');
  const [safetyCompleted, setSafetyCompleted] = useState(false);
  const [completedPhases, setCompletedPhases] = useState([]);
  
  // Session form state
  const [sessionForm, setSessionForm] = useState({
    measurement_date: new Date().toISOString().split('T')[0],
    shift: 'Day',
    operating_condition: 'Normal',
    weather: '',
    client_escort: '',
    start_time: '',
    end_time: '',
    remarks: ''
  });
  
  // Safety checklist state
  const [safetyChecklist, setSafetyChecklist] = useState(DEFAULT_SAFETY_CHECKLIST);
  
  // SEU state
  const [availableSEUs, setAvailableSEUs] = useState([]);
  const [linkedSEUs, setLinkedSEUs] = useState([]);
  const [selectedSEU, setSelectedSEU] = useState(null);
  
  // Electrical measurement state
  const [electricalSnapshot, setElectricalSnapshot] = useState({
    avg_voltage_v: '', avg_current_a: '', avg_power_kw: '', avg_kva: '',
    power_factor: '', frequency_hz: '', idle_power_kw: '', max_demand_kw: '',
    instrument_used: '', remarks: ''
  });
  
  const [powerQuality, setPowerQuality] = useState({
    thd_voltage_percent: '', thd_current_percent: '',
    phase_imbalance_percent: '', neutral_current_a: '', remarks: ''
  });
  
  const [loadLog, setLoadLog] = useState([]);
  
  // Thermal measurement state
  const [thermalData, setThermalData] = useState({
    stack_temp_c: '', ambient_temp_c: '', o2_percent: '', co_ppm: '',
    excess_air_percent: '', flue_gas_loss_percent: '', estimated_efficiency_percent: '',
    fuel_flow_rate: '', fuel_flow_unit: 'SCM/hr', instrument_used: '', remarks: ''
  });
  
  const [steamData, setSteamData] = useState({
    steam_traps_checked: '', steam_traps_leaking: '', condensate_recovery: '',
    insulation_condition: '', visible_leaks: false, remarks: ''
  });
  
  // Water/Waste observation state
  const [waterData, setWaterData] = useState({
    visible_leaks: false, cooling_tower_coc: '', excess_blowdown: false,
    reuse_possible: false, ro_reject_to_drain: false, daily_consumption_kl: '', remarks: ''
  });
  
  const [wasteData, setWasteData] = useState({
    waste_stream: '', handling_method: '', moisture_content: '',
    reduction_possible: false, segregation_in_place: false, monthly_qty: '', unit: 'MT', remarks: ''
  });
  
  // Observations state
  const [observations, setObservations] = useState([]);
  const [newObservation, setNewObservation] = useState({
    observation_type: '', description: '', severity: 'Medium',
    efficiency_impact: 'Medium', safety_risk: false, probable_cause: ''
  });
  
  // Verification state
  const [electricalVerification, setElectricalVerification] = useState([
    { item: "Motor nameplate readable", checked: false, remarks: "" },
    { item: "Motor load <50%", checked: false, remarks: "" },
    { item: "VFD present", checked: false, remarks: "" },
    { item: "Throttling observed", checked: false, remarks: "" },
    { item: "APFC installed", checked: false, remarks: "" },
    { item: "Harmonics suspected", checked: false, remarks: "" },
    { item: "Sub-meter available", checked: false, remarks: "" }
  ]);
  
  const [thermalVerification, setThermalVerification] = useState([
    { item: "Stack temp measured", checked: false, remarks: "" },
    { item: "O₂ measured", checked: false, remarks: "" },
    { item: "CO within limits", checked: false, remarks: "" },
    { item: "Blowdown monitored", checked: false, remarks: "" },
    { item: "Economizer present", checked: false, remarks: "" },
    { item: "Condensate return >70%", checked: false, remarks: "" }
  ]);
  
  // Insights state
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [aiNarrative, setAiNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  
  // Summary state
  const [sessionSummary, setSessionSummary] = useState(null);
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, session: null });
  const [deleting, setDeleting] = useState(false);
  
  // Read-only mode for closed sessions
  const isReadOnly = activeSession?.status === 'Closed';
  
  // ==================== DATA FETCHING ====================
  
  const fetchStudyDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const studyRes = await axios.get(`${API}/api/ers/studies/${studyId}`, config);
      setStudy(studyRes.data);
      
      if (studyRes.data.site_id) {
        const siteRes = await axios.get(`${API}/api/ers/sites/${studyRes.data.site_id}`, config);
        setSite(siteRes.data);
      }
    } catch (error) {
      console.error('Error fetching study:', error);
    }
  }, [studyId]);
  
  const fetchSessions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.get(`${API}/api/ers/onsite/sessions/${studyId}`, config);
      setSessions(res.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  }, [studyId]);
  
  const fetchAvailableSEUs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.get(`${API}/api/ers/onsite/seus/${studyId}`, config);
      setAvailableSEUs(res.data);
    } catch (error) {
      console.error('Error fetching SEUs:', error);
    }
  }, [studyId]);
  
  const fetchSessionDetails = useCallback(async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.get(`${API}/api/ers/onsite/session/${sessionId}`, config);
      setActiveSession(res.data.session);
      setLinkedSEUs(res.data.linked_seus || []);
      setSafetyChecklist(res.data.session.safety_checklist || DEFAULT_SAFETY_CHECKLIST);
      setSafetyCompleted(res.data.session.safety_completed || false);
    } catch (error) {
      console.error('Error fetching session details:', error);
    }
  }, []);
  
  const fetchElectricalData = useCallback(async (sessionId, seuId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.get(`${API}/api/ers/onsite/electrical/${sessionId}/${seuId}`, config);

      // Always reset to the response shape — if a SEU has no saved data, the
      // previous SEU's values must not bleed through.
      if (res.data.snapshot) {
        setElectricalSnapshot({
          avg_voltage_v: res.data.snapshot.avg_voltage_v || '',
          avg_current_a: res.data.snapshot.avg_current_a || '',
          avg_power_kw: res.data.snapshot.avg_power_kw || '',
          avg_kva: res.data.snapshot.avg_kva || '',
          power_factor: res.data.snapshot.power_factor || '',
          frequency_hz: res.data.snapshot.frequency_hz || '',
          idle_power_kw: res.data.snapshot.idle_power_kw || '',
          max_demand_kw: res.data.snapshot.max_demand_kw || '',
          instrument_used: res.data.snapshot.instrument_used || '',
          remarks: res.data.snapshot.remarks || ''
        });
      } else {
        setElectricalSnapshot({
          avg_voltage_v: '', avg_current_a: '', avg_power_kw: '', avg_kva: '',
          power_factor: '', frequency_hz: '', idle_power_kw: '', max_demand_kw: '',
          instrument_used: '', remarks: ''
        });
      }

      if (res.data.power_quality) {
        setPowerQuality({
          thd_voltage_percent: res.data.power_quality.thd_voltage_percent || '',
          thd_current_percent: res.data.power_quality.thd_current_percent || '',
          phase_imbalance_percent: res.data.power_quality.phase_imbalance_percent || '',
          neutral_current_a: res.data.power_quality.neutral_current_a || '',
          remarks: res.data.power_quality.remarks || ''
        });
      } else {
        setPowerQuality({
          thd_voltage_percent: '', thd_current_percent: '',
          phase_imbalance_percent: '', neutral_current_a: '', remarks: ''
        });
      }

      if (res.data.load_log?.entries) {
        setLoadLog(res.data.load_log.entries);
      } else {
        setLoadLog([]);
      }
    } catch (error) {
      console.error('Error fetching electrical data:', error);
    }
  }, []);
  
  const fetchThermalData = useCallback(async (sessionId, seuId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.get(`${API}/api/ers/onsite/thermal/${sessionId}/${seuId}`, config);

      // Always reset to the response shape — if a SEU has no saved data, the
      // previous SEU's values must not bleed through.
      if (res.data.combustion) {
        setThermalData({
          stack_temp_c: res.data.combustion.stack_temp_c || '',
          ambient_temp_c: res.data.combustion.ambient_temp_c || '',
          o2_percent: res.data.combustion.o2_percent || '',
          co_ppm: res.data.combustion.co_ppm || '',
          excess_air_percent: res.data.combustion.excess_air_percent || '',
          flue_gas_loss_percent: res.data.combustion.flue_gas_loss_percent || '',
          estimated_efficiency_percent: res.data.combustion.estimated_efficiency_percent || '',
          fuel_flow_rate: res.data.combustion.fuel_flow_rate || '',
          fuel_flow_unit: res.data.combustion.fuel_flow_unit || 'SCM/hr',
          instrument_used: res.data.combustion.instrument_used || '',
          remarks: res.data.combustion.remarks || ''
        });
      } else {
        setThermalData({
          stack_temp_c: '', ambient_temp_c: '', o2_percent: '', co_ppm: '',
          excess_air_percent: '', flue_gas_loss_percent: '', estimated_efficiency_percent: '',
          fuel_flow_rate: '', fuel_flow_unit: 'SCM/hr', instrument_used: '', remarks: ''
        });
      }

      if (res.data.steam) {
        setSteamData({
          steam_traps_checked: res.data.steam.steam_traps_checked || '',
          steam_traps_leaking: res.data.steam.steam_traps_leaking || '',
          condensate_recovery: res.data.steam.condensate_recovery || '',
          insulation_condition: res.data.steam.insulation_condition || '',
          visible_leaks: res.data.steam.visible_leaks || false,
          remarks: res.data.steam.remarks || ''
        });
      } else {
        setSteamData({
          steam_traps_checked: '', steam_traps_leaking: '', condensate_recovery: '',
          insulation_condition: '', visible_leaks: false, remarks: ''
        });
      }
    } catch (error) {
      console.error('Error fetching thermal data:', error);
    }
  }, []);
  
  const fetchWaterWasteData = useCallback(async (sessionId, seuId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.get(`${API}/api/ers/onsite/water-waste/${sessionId}/${seuId}`, config);

      // Always reset to the response shape — if a SEU has no saved data, the
      // previous SEU's values must not bleed through.
      if (res.data.water) {
        setWaterData({
          visible_leaks: res.data.water.visible_leaks || false,
          cooling_tower_coc: res.data.water.cooling_tower_coc || '',
          excess_blowdown: res.data.water.excess_blowdown || false,
          reuse_possible: res.data.water.reuse_possible || false,
          ro_reject_to_drain: res.data.water.ro_reject_to_drain || false,
          daily_consumption_kl: res.data.water.daily_consumption_kl || '',
          remarks: res.data.water.remarks || ''
        });
      } else {
        setWaterData({
          visible_leaks: false, cooling_tower_coc: '', excess_blowdown: false,
          reuse_possible: false, ro_reject_to_drain: false, daily_consumption_kl: '', remarks: ''
        });
      }

      if (res.data.waste) {
        setWasteData({
          waste_stream: res.data.waste.waste_stream || '',
          handling_method: res.data.waste.handling_method || '',
          moisture_content: res.data.waste.moisture_content || '',
          reduction_possible: res.data.waste.reduction_possible || false,
          segregation_in_place: res.data.waste.segregation_in_place || false,
          monthly_qty: res.data.waste.monthly_qty || '',
          unit: res.data.waste.unit || 'MT',
          remarks: res.data.waste.remarks || ''
        });
      } else {
        setWasteData({
          waste_stream: '', handling_method: '', moisture_content: '',
          reduction_possible: false, segregation_in_place: false,
          monthly_qty: '', unit: 'MT', remarks: ''
        });
      }
    } catch (error) {
      console.error('Error fetching water/waste data:', error);
    }
  }, []);
  
  const fetchObservations = useCallback(async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.get(`${API}/api/ers/onsite/observations/${sessionId}`, config);
      setObservations(res.data || []);
    } catch (error) {
      console.error('Error fetching observations:', error);
    }
  }, []);
  
  const fetchInsights = useCallback(async (sessionId) => {
    setInsightsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.get(`${API}/api/ers/onsite/insights/${sessionId}`, config);
      setInsights(res.data);
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  }, []);
  
  const fetchVerificationData = useCallback(async (sessionId, seuId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/api/ers/onsite/verification/${sessionId}/${seuId}`, config);
      if (Array.isArray(res.data)) {
        for (const ver of res.data) {
          const savedItems = ver.items || [];
          if (savedItems.length > 0) {
            if (ver.checklist_type === 'electrical') {
              setElectricalVerification(prev =>
                prev.map(item => {
                  const saved = savedItems.find(s => s.item === item.item);
                  return saved ? { ...item, checked: saved.checked, remarks: saved.remarks || '' } : item;
                })
              );
            } else if (ver.checklist_type === 'thermal') {
              setThermalVerification(prev =>
                prev.map(item => {
                  const saved = savedItems.find(s => s.item === item.item);
                  return saved ? { ...item, checked: saved.checked, remarks: saved.remarks || '' } : item;
                })
              );
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching verification data:', error);
    }
  }, []);

  const generateAiNarrative = useCallback(async (sessionId) => {
    setNarrativeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.post(`${API}/api/ers/onsite/sessions/${sessionId}/generate-insights`, {}, config);
      setAiNarrative(res.data);
    } catch (error) {
      console.error('Error generating AI narrative:', error);
      showError('Failed to generate AI insights. Please try again.');
    } finally {
      setNarrativeLoading(false);
    }
  }, []);
  
  const fetchSessionSummary = useCallback(async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.get(`${API}/api/ers/onsite/summary/${sessionId}`, config);
      setSessionSummary(res.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, []);
  
  // ==================== EFFECTS ====================
  
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        fetchStudyDetails(),
        fetchSessions(),
        fetchAvailableSEUs()
      ]);
      setLoading(false);
    };
    init();
  }, [fetchStudyDetails, fetchSessions, fetchAvailableSEUs]);
  
  useEffect(() => {
    if (selectedSEU && activeSession) {
      // Reset verification checklists to defaults before loading saved data
      setElectricalVerification([
        { item: "Motor nameplate readable", checked: false, remarks: "" },
        { item: "Motor load <50%", checked: false, remarks: "" },
        { item: "VFD present", checked: false, remarks: "" },
        { item: "Throttling observed", checked: false, remarks: "" },
        { item: "APFC installed", checked: false, remarks: "" },
        { item: "Harmonics suspected", checked: false, remarks: "" },
        { item: "Sub-meter available", checked: false, remarks: "" }
      ]);
      setThermalVerification([
        { item: "Stack temp measured", checked: false, remarks: "" },
        { item: "O₂ measured", checked: false, remarks: "" },
        { item: "CO within limits", checked: false, remarks: "" },
        { item: "Blowdown monitored", checked: false, remarks: "" },
        { item: "Economizer present", checked: false, remarks: "" },
        { item: "Condensate return >70%", checked: false, remarks: "" }
      ]);

      if (selectedSEU.seu_type === 'electricity') {
        fetchElectricalData(activeSession.id, selectedSEU.seu_id);
      } else if (selectedSEU.seu_type === 'fuel') {
        fetchThermalData(activeSession.id, selectedSEU.seu_id);
      } else if (selectedSEU.seu_type === 'water' || selectedSEU.seu_type === 'waste') {
        fetchWaterWasteData(activeSession.id, selectedSEU.seu_id);
      }

      // Load saved verification data
      fetchVerificationData(activeSession.id, selectedSEU.seu_id);
    }
  }, [selectedSEU, activeSession, fetchElectricalData, fetchThermalData, fetchWaterWasteData, fetchVerificationData]);
  
  useEffect(() => {
    if (activeSession && activePhase === 'observations') {
      fetchObservations(activeSession.id);
    }
    if (activeSession && activePhase === 'insights') {
      fetchInsights(activeSession.id);
    }
    if (activeSession && activePhase === 'summary') {
      fetchSessionSummary(activeSession.id);
    }
  }, [activeSession, activePhase, fetchObservations, fetchInsights, fetchSessionSummary]);
  
  // ==================== HANDLERS ====================
  
  const handleCreateSession = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        study_id: studyId,
        site_id: study?.site_id,
        ...sessionForm
      };
      
      const res = await axios.post(`${API}/api/ers/onsite/sessions`, payload, config);
      await fetchSessions();
      setActiveSession(res.data);
      setSafetyCompleted(false);
      setShowNewSession(false);
      setActivePhase('safety');
    } catch (error) {
      console.error('Error creating session:', error);
      showError('Failed to create session');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveSafetyChecklist = async () => {
    if (!activeSession) return;
    
    // Check mandatory items
    const mandatoryItems = safetyChecklist.filter(item => item.mandatory);
    const allMandatoryChecked = mandatoryItems.every(item => item.checked);

    if (!allMandatoryChecked) {
      showError('Please complete all mandatory safety items (marked with *)');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(
        `${API}/api/ers/onsite/session/${activeSession.id}/complete-safety`,
        safetyChecklist,
        config
      );
      
      setSafetyCompleted(true);
      setCompletedPhases([...completedPhases, 'safety']);
      await fetchSessionDetails(activeSession.id);
    } catch (error) {
      console.error('Error saving safety checklist:', error);
      showError(error.response?.data?.detail || 'Failed to save safety checklist');
    } finally {
      setSaving(false);
    }
  };
  
  const handleLinkSEU = async (seu) => {
    if (!activeSession) return;
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(
        `${API}/api/ers/onsite/session/${activeSession.id}/link-seu?seu_id=${seu.seu_id}&seu_type=${seu.type}`,
        {},
        config
      );
      
      await fetchSessionDetails(activeSession.id);
    } catch (error) {
      console.error('Error linking SEU:', error);
      showError(error.response?.data?.detail || 'Failed to link SEU');
    }
  };
  
  const handleUnlinkSEU = async (seuId) => {
    if (!activeSession) return;
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(
        `${API}/api/ers/onsite/session/${activeSession.id}/unlink-seu/${seuId}`,
        config
      );
      
      if (selectedSEU?.seu_id === seuId) {
        setSelectedSEU(null);
      }
      
      await fetchSessionDetails(activeSession.id);
    } catch (error) {
      console.error('Error unlinking SEU:', error);
    }
  };
  
  // Delete session handler
  const handleDeleteSession = async () => {
    if (!deleteConfirm.session) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API}/api/ers/onsite/sessions/${deleteConfirm.session.id}`, config);
      
      // Clear delete confirm state
      setDeleteConfirm({ show: false, session: null });
      
      // Refresh sessions list
      await fetchSessions();
      
      // If the deleted session was active, clear it
      if (activeSession?.id === deleteConfirm.session.id) {
        setActiveSession(null);
        setLinkedSEUs([]);
        setSelectedSEU(null);
      }
      
      showSuccess(`Session ${deleteConfirm.session.session_id} deleted successfully`);
    } catch (error) {
      console.error('Error deleting session:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete session';
      showError(errorMsg);
    } finally {
      setDeleting(false);
    }
  };
  
  const handleSaveElectricalSnapshot = async () => {
    if (!activeSession || !selectedSEU) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        session_id: activeSession.id,
        seu_id: selectedSEU.seu_id,
        ...Object.fromEntries(
          Object.entries(electricalSnapshot).map(([k, v]) => [k, v === '' ? null : v])
        )
      };
      
      await axios.post(`${API}/api/ers/onsite/electrical/snapshot`, payload, config);
      showSuccess('Electrical measurements saved!');
    } catch (error) {
      console.error('Error saving electrical snapshot:', error);
      showError('Failed to save measurements');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSavePowerQuality = async () => {
    if (!activeSession || !selectedSEU) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        session_id: activeSession.id,
        seu_id: selectedSEU.seu_id,
        ...Object.fromEntries(
          Object.entries(powerQuality).map(([k, v]) => [k, v === '' ? null : v])
        )
      };
      
      await axios.post(`${API}/api/ers/onsite/electrical/power-quality`, payload, config);
      showSuccess('Power quality data saved!');
    } catch (error) {
      console.error('Error saving power quality:', error);
    } finally {
      setSaving(false);
    }
  };
  
  const handleAddLoadLogEntry = () => {
    setLoadLog([...loadLog, {
      timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
      power_kw: '', current_a: '', pf: '', operating_state: 'Load'
    }]);
  };
  
  const handleSaveLoadLog = async () => {
    if (!activeSession || !selectedSEU) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        session_id: activeSession.id,
        seu_id: selectedSEU.seu_id,
        entries: loadLog.map(entry => ({
          ...entry,
          power_kw: entry.power_kw === '' ? null : parseFloat(entry.power_kw),
          current_a: entry.current_a === '' ? null : parseFloat(entry.current_a),
          pf: entry.pf === '' ? null : parseFloat(entry.pf)
        }))
      };
      
      await axios.post(`${API}/api/ers/onsite/electrical/load-log`, payload, config);
      showSuccess('Load log saved!');
    } catch (error) {
      console.error('Error saving load log:', error);
    } finally {
      setSaving(false);
    }
  };
  
  // Thermal save handlers
  const handleSaveThermalCombustion = async () => {
    if (!activeSession || !selectedSEU) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        session_id: activeSession.id,
        seu_id: selectedSEU.seu_id,
        ...Object.fromEntries(
          Object.entries(thermalData).map(([k, v]) => [k, v === '' ? null : v])
        )
      };
      
      await axios.post(`${API}/api/ers/onsite/thermal/combustion`, payload, config);
      showSuccess('Combustion data saved!');
    } catch (error) {
      console.error('Error saving thermal data:', error);
      showError('Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveSteam = async () => {
    if (!activeSession || !selectedSEU) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        session_id: activeSession.id,
        seu_id: selectedSEU.seu_id,
        ...steamData
      };
      
      await axios.post(`${API}/api/ers/onsite/thermal/steam`, payload, config);
      showSuccess('Steam observations saved!');
    } catch (error) {
      console.error('Error saving steam data:', error);
    } finally {
      setSaving(false);
    }
  };
  
  // Water/Waste save handlers
  const handleSaveWater = async () => {
    if (!activeSession || !selectedSEU) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        session_id: activeSession.id,
        seu_id: selectedSEU.seu_id,
        ...waterData
      };
      
      await axios.post(`${API}/api/ers/onsite/water/observation`, payload, config);
      showSuccess('Water observations saved!');
    } catch (error) {
      console.error('Error saving water data:', error);
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveWaste = async () => {
    if (!activeSession || !selectedSEU) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        session_id: activeSession.id,
        seu_id: selectedSEU.seu_id,
        ...wasteData
      };
      
      await axios.post(`${API}/api/ers/onsite/waste/observation`, payload, config);
      showSuccess('Waste observations saved!');
    } catch (error) {
      console.error('Error saving waste data:', error);
    } finally {
      setSaving(false);
    }
  };
  
  // Observation handlers
  const handleAddObservation = async () => {
    if (!activeSession || !selectedSEU) return;
    if (!newObservation.observation_type || !newObservation.description) {
      showError('Please fill in observation type and description');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(
        `${API}/api/ers/onsite/observations/${activeSession.id}/${selectedSEU.seu_id}`,
        newObservation,
        config
      );
      
      await fetchObservations(activeSession.id);
      setNewObservation({
        observation_type: '', description: '', severity: 'Medium',
        efficiency_impact: 'Medium', safety_risk: false, probable_cause: ''
      });
    } catch (error) {
      console.error('Error adding observation:', error);
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteObservation = async (obsId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API}/api/ers/onsite/observations/${obsId}`, config);
      await fetchObservations(activeSession.id);
    } catch (error) {
      console.error('Error deleting observation:', error);
    }
  };
  
  // Verification save handler
  const handleSaveVerification = async (type) => {
    if (!activeSession || !selectedSEU) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const checklist = type === 'electrical' ? electricalVerification : thermalVerification;
      
      await axios.post(
        `${API}/api/ers/onsite/verification/${activeSession.id}/${selectedSEU.seu_id}?checklist_type=${type}`,
        checklist,
        config
      );
      showSuccess('Verification checklist saved!');
    } catch (error) {
      console.error('Error saving verification:', error);
    } finally {
      setSaving(false);
    }
  };
  
  // Close session handler
  const handleCloseSession = async () => {
    if (!activeSession) return;

    const confirmed = await confirm(
      'Close Session',
      'Are you sure you want to close this session? This action cannot be undone.',
      { confirmLabel: 'Close Session', destructive: true }
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(`${API}/api/ers/onsite/session/${activeSession.id}/close`, {}, config);
      showSuccess('Session closed successfully!');
      navigate(`/ers/study/${studyId}?tab=onsite-study`);
    } catch (error) {
      console.error('Error closing session:', error);
      showError('Failed to close session');
    } finally {
      setSaving(false);
    }
  };
  
  // ==================== RENDER PHASES ====================
  
  const renderSafetyPhase = () => (
    <div className="space-y-6">
      {/* Session Selection or Creation */}
      {!activeSession ? (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Start Measurement Session</h3>
              <p className="text-sm text-gray-500">Create or continue a measurement session</p>
            </div>
          </div>
          
          {sessions.length > 0 && !showNewSession && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Continue Previous Session</h4>
              <div className="space-y-2">
                {sessions.filter(s => s.status !== 'Closed').map(session => (
                  <div
                    key={session.id}
                    className="p-4 border rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all flex items-center justify-between group"
                  >
                    <div 
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={() => fetchSessionDetails(session.id)}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        session.safety_completed ? 'bg-green-100' : 'bg-yellow-100'
                      }`}>
                        {session.safety_completed ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Shield className="w-5 h-5 text-yellow-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{session.session_id}</p>
                        <p className="text-sm text-gray-500">
                          {session.measurement_date} • {session.shift} • {session.operating_condition}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm({ show: true, session });
                        }}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                        title="Delete session"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Closed Sessions - View Only */}
          {sessions.filter(s => s.status === 'Closed').length > 0 && !showNewSession && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-500" />
                View Completed Sessions (Read Only)
              </h4>
              <div className="space-y-2">
                {sessions.filter(s => s.status === 'Closed').map(session => (
                  <div
                    key={session.id}
                    onClick={() => {
                      fetchSessionDetails(session.id);
                      // Mark as read-only mode
                    }}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-all flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100">
                        <Lock className="w-5 h-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-700">{session.session_id}</p>
                        <p className="text-sm text-gray-500">
                          {session.measurement_date} • {session.shift} • {session.operating_condition}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                        Completed
                      </span>
                      <Eye className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {!showNewSession ? (
            <button
              onClick={() => setShowNewSession(true)}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 font-medium hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create New Session
            </button>
          ) : (
            <div className="bg-blue-50 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-900">New Session Details</h4>
                <button onClick={() => setShowNewSession(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                  <DatePicker
                    value={sessionForm.measurement_date}
                    onChange={(value) => setSessionForm({...sessionForm, measurement_date: value})}
                    placeholder="Select date"
                    dateFormat="PP"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Shift</label>
                  <select
                    value={sessionForm.shift}
                    onChange={(e) => setSessionForm({...sessionForm, shift: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    {SHIFTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Condition</label>
                  <select
                    value={sessionForm.operating_condition}
                    onChange={(e) => setSessionForm({...sessionForm, operating_condition: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    {OPERATING_CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Weather</label>
                  <select
                    value={sessionForm.weather}
                    onChange={(e) => setSessionForm({...sessionForm, weather: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="">Select...</option>
                    {WEATHER_OPTIONS.map(w => <option key={w} value={w}>{w}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Client Escort</label>
                  <input
                    type="text"
                    value={sessionForm.client_escort}
                    onChange={(e) => setSessionForm({...sessionForm, client_escort: e.target.value})}
                    placeholder="Name"
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={sessionForm.start_time}
                    onChange={(e) => setSessionForm({...sessionForm, start_time: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>
              
              <button
                onClick={handleCreateSession}
                disabled={saving}
                className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Creating...' : 'Start Session'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Read-Only Banner for Closed Sessions */}
          {isReadOnly && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-full">
                <Lock className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-amber-800">Read-Only Mode</p>
                <p className="text-sm text-amber-600">
                  This session has been closed and locked. You can view all measurements and observations but cannot make changes.
                </p>
              </div>
              <button
                onClick={() => {
                  setActiveSession(null);
                  setLinkedSEUs([]);
                  setSelectedSEU(null);
                  setSafetyCompleted(false);
                }}
                className="px-4 py-2 bg-amber-200 text-amber-800 rounded-lg font-medium hover:bg-amber-300 text-sm"
              >
                Back to Sessions
              </button>
            </div>
          )}
          
          {/* Active Session Header */}
          <div className={`rounded-xl p-4 text-white ${isReadOnly ? 'bg-gradient-to-r from-gray-500 to-gray-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isReadOnly ? 'text-gray-200' : 'text-blue-100'}`}>
                  {isReadOnly ? 'Viewing Completed Session' : 'Active Session'}
                </p>
                <p className="text-xl font-bold">{activeSession.session_id}</p>
                <p className={`text-sm ${isReadOnly ? 'text-gray-300' : 'text-blue-200'}`}>
                  {activeSession.measurement_date} • {activeSession.shift} Shift • {activeSession.operating_condition}
                </p>
              </div>
              <button
                onClick={() => {
                  setActiveSession(null);
                  setLinkedSEUs([]);
                  setSelectedSEU(null);
                  setSafetyCompleted(false);
                }}
                className="px-3 py-1.5 bg-white/20 rounded-lg text-sm hover:bg-white/30"
              >
                Change
              </button>
            </div>
          </div>
          
          {/* Safety Checklist */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-red-50 to-orange-50 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  safetyCompleted ? 'bg-green-500' : 'bg-red-500'
                }`}>
                  {safetyCompleted ? (
                    <CheckCircle className="w-5 h-5 text-white" />
                  ) : (
                    <Shield className="w-5 h-5 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Safety & Permit Checklist</h3>
                  <p className="text-sm text-gray-500">
                    {safetyCompleted ? 'Completed - All phases unlocked' : 'Complete to unlock measurements'}
                  </p>
                </div>
              </div>
              {!safetyCompleted && (
                <button
                  onClick={handleSaveSafetyChecklist}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Accept & Proceed'}
                </button>
              )}
            </div>
            
            <div className="p-4 space-y-2">
              {safetyChecklist.map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    safetyCompleted || isReadOnly ? 'bg-gray-50' : item.checked ? 'bg-green-50' : 'bg-white border'
                  }`}
                >
                  <button
                    onClick={() => {
                      if (!safetyCompleted && !isReadOnly) {
                        const updated = [...safetyChecklist];
                        updated[index].checked = !updated[index].checked;
                        setSafetyChecklist(updated);
                      }
                    }}
                    disabled={safetyCompleted || isReadOnly}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      item.checked 
                        ? 'bg-green-500 border-green-500 text-white' 
                        : 'border-gray-300 hover:border-green-400'
                    } ${safetyCompleted || isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {item.checked && <Check className="w-4 h-4" />}
                  </button>
                  <span className={`flex-1 text-sm ${item.checked ? 'text-gray-900' : 'text-gray-600'}`}>
                    {item.item}
                    {item.mandatory && <span className="text-red-500 ml-1">*</span>}
                  </span>
                  <input
                    type="text"
                    value={item.remarks}
                    onChange={(e) => {
                      if (!safetyCompleted) {
                        const updated = [...safetyChecklist];
                        updated[index].remarks = e.target.value;
                        setSafetyChecklist(updated);
                      }
                    }}
                    disabled={safetyCompleted}
                    placeholder="Remarks"
                    className="w-32 px-2 py-1 text-sm border rounded bg-white disabled:bg-gray-100"
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
  
  const renderSEUSelectionPhase = () => (
    <div className="space-y-6">
      {/* Selected SEUs */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          Selected for Measurement ({linkedSEUs.length})
        </h3>
        
        {linkedSEUs.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No SEUs selected. Choose from below.</p>
        ) : (
          <div className="grid gap-3">
            {linkedSEUs.map(link => (
              <div
                key={link.id}
                onClick={() => setSelectedSEU(link)}
                className={`p-4 border rounded-lg cursor-pointer transition-all flex items-center justify-between ${
                  selectedSEU?.seu_id === link.seu_id 
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                    : 'hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    link.seu_type === 'electricity' ? 'bg-yellow-100' :
                    link.seu_type === 'fuel' ? 'bg-orange-100' :
                    link.seu_type === 'water' ? 'bg-cyan-100' : 'bg-green-100'
                  }`}>
                    {link.seu_type === 'electricity' ? <Zap className="w-5 h-5 text-yellow-600" /> :
                     link.seu_type === 'fuel' ? <Flame className="w-5 h-5 text-orange-600" /> :
                     link.seu_type === 'water' ? <Droplets className="w-5 h-5 text-cyan-600" /> :
                     <Trash className="w-5 h-5 text-green-600" />}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{link.seu_code}</p>
                    <p className="text-sm text-gray-500">{link.desktop_data?.equipment_type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    link.measurement_status === 'Complete' ? 'bg-green-100 text-green-700' :
                    link.measurement_status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {link.measurement_status}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleUnlinkSEU(link.seu_id); }}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Available SEUs */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          Available from Desktop Study ({availableSEUs.filter(s => !linkedSEUs.some(l => l.seu_id === s.seu_id)).length})
        </h3>
        
        <div className="grid gap-2">
          {availableSEUs
            .filter(seu => !linkedSEUs.some(l => l.seu_id === seu.seu_id))
            .map(seu => (
              <div
                key={seu.seu_id}
                className="p-3 border rounded-lg flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    seu.type === 'electricity' ? 'bg-yellow-100' :
                    seu.type === 'fuel' ? 'bg-orange-100' :
                    seu.type === 'water' ? 'bg-cyan-100' : 'bg-green-100'
                  }`}>
                    {seu.type === 'electricity' ? <Zap className="w-4 h-4 text-yellow-600" /> :
                     seu.type === 'fuel' ? <Flame className="w-4 h-4 text-orange-600" /> :
                     seu.type === 'water' ? <Droplets className="w-4 h-4 text-cyan-600" /> :
                     <Trash className="w-4 h-4 text-green-600" />}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{seu.seu_code}</p>
                    <p className="text-xs text-gray-500">{seu.equipment} • {seu.system}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleLinkSEU(seu)}
                  className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200"
                >
                  Select
                </button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
  
  const renderElectricalPhase = () => (
    <div className="space-y-6">
      {/* SEU Selector */}
      <SEUSelector filterType="electricity" color="yellow" icon={Zap} />
      
      {!selectedSEU || selectedSEU.seu_type !== 'electricity' ? (
        linkedSEUs.filter(s => s.seu_type === 'electricity').length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
            <Zap className="w-10 h-10 text-blue-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Select an electrical SEU from above to enter measurements</p>
          </div>
        )
      ) : (
        <>
          {/* Selected SEU Header */}
          <div className="bg-gradient-to-r from-yellow-400 to-orange-400 rounded-xl p-4 text-white">
            <div className="flex items-center gap-3">
              <Zap className="w-8 h-8" />
              <div>
                <p className="font-bold text-lg">{selectedSEU.seu_code}</p>
                <p className="text-yellow-100">{selectedSEU.desktop_data?.equipment_type} • {selectedSEU.desktop_data?.system_name}</p>
              </div>
            </div>
          </div>
          
          {/* Desktop Context */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm font-medium text-gray-600 mb-2">Desktop Reference</p>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div><p className="text-lg font-bold text-gray-900">{selectedSEU.desktop_data?.rated_capacity || '-'}</p><p className="text-xs text-gray-500">kW Rated</p></div>
              <div><p className="text-lg font-bold text-gray-900">{selectedSEU.desktop_data?.load_percent || '-'}%</p><p className="text-xs text-gray-500">Load</p></div>
              <div><p className="text-lg font-bold text-gray-900">{selectedSEU.desktop_data?.hours_per_day || '-'}</p><p className="text-xs text-gray-500">Hrs/Day</p></div>
              <div><p className="text-lg font-bold text-gray-900">{(selectedSEU.desktop_data?.final_annual_energy / 1000 || 0).toFixed(0)}k</p><p className="text-xs text-gray-500">kWh/yr</p></div>
            </div>
          </div>
          
          {/* Snapshot Measurements */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Gauge className="w-5 h-5 text-blue-600" />
                Snapshot Readings
              </h3>
              <button
                onClick={handleSaveElectricalSnapshot}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'avg_voltage_v', label: 'Voltage', unit: 'V', placeholder: '415' },
                { key: 'avg_current_a', label: 'Current', unit: 'A', placeholder: '245' },
                { key: 'avg_power_kw', label: 'Power', unit: 'kW', placeholder: '138' },
                { key: 'avg_kva', label: 'Apparent', unit: 'kVA', placeholder: '158' },
                { key: 'power_factor', label: 'PF', unit: '', placeholder: '0.87' },
                { key: 'frequency_hz', label: 'Freq', unit: 'Hz', placeholder: '50' },
                { key: 'idle_power_kw', label: 'Idle', unit: 'kW', placeholder: '32' },
                { key: 'max_demand_kw', label: 'Max', unit: 'kW', placeholder: '145' }
              ].map(field => (
                <div key={field.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {field.label} {field.unit && <span className="text-gray-400">({field.unit})</span>}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={electricalSnapshot[field.key]}
                    onChange={(e) => setElectricalSnapshot({...electricalSnapshot, [field.key]: e.target.value})}
                    placeholder={field.placeholder}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              ))}
            </div>
            
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Instrument</label>
                <select
                  value={electricalSnapshot.instrument_used}
                  onChange={(e) => setElectricalSnapshot({...electricalSnapshot, instrument_used: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">Select...</option>
                  {INSTRUMENTS.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
                <input
                  type="text"
                  value={electricalSnapshot.remarks}
                  onChange={(e) => setElectricalSnapshot({...electricalSnapshot, remarks: e.target.value})}
                  placeholder="Notes..."
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>
          </div>
          
          {/* Power Quality */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-600" />
                Power Quality
              </h3>
              <button
                onClick={handleSavePowerQuality}
                disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 text-sm"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">V-THD %</label>
                <input
                  type="number"
                  step="0.1"
                  value={powerQuality.thd_voltage_percent}
                  onChange={(e) => setPowerQuality({...powerQuality, thd_voltage_percent: e.target.value})}
                  placeholder="4.8"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">I-THD %</label>
                <input
                  type="number"
                  step="0.1"
                  value={powerQuality.thd_current_percent}
                  onChange={(e) => setPowerQuality({...powerQuality, thd_current_percent: e.target.value})}
                  placeholder="18.2"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phase Imbal %</label>
                <input
                  type="number"
                  step="0.1"
                  value={powerQuality.phase_imbalance_percent}
                  onChange={(e) => setPowerQuality({...powerQuality, phase_imbalance_percent: e.target.value})}
                  placeholder="2.5"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Neutral A</label>
                <input
                  type="number"
                  value={powerQuality.neutral_current_a}
                  onChange={(e) => setPowerQuality({...powerQuality, neutral_current_a: e.target.value})}
                  placeholder="38"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>
          </div>
          
          {/* Load Log */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-green-600" />
                Load Log <span className="text-sm font-normal text-gray-500">(Optional)</span>
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={handleAddLoadLogEntry}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Add
                </button>
                {loadLog.length > 0 && (
                  <button
                    onClick={handleSaveLoadLog}
                    disabled={saving}
                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                  >
                    Save Log
                  </button>
                )}
              </div>
            </div>
            
            {loadLog.length === 0 ? (
              <p className="text-gray-500 text-center py-4 text-sm">No entries. Click Add to start logging.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Time</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">kW</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">A</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">PF</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">State</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {loadLog.map((entry, index) => (
                      <tr key={index}>
                        <td className="px-3 py-2">
                          <input type="time" value={entry.timestamp} onChange={(e) => {
                            const updated = [...loadLog];
                            updated[index].timestamp = e.target.value;
                            setLoadLog(updated);
                          }} className="w-24 px-2 py-1 border rounded text-sm" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" value={entry.power_kw} onChange={(e) => {
                            const updated = [...loadLog];
                            updated[index].power_kw = e.target.value;
                            setLoadLog(updated);
                          }} placeholder="kW" className="w-16 px-2 py-1 border rounded text-sm" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" value={entry.current_a} onChange={(e) => {
                            const updated = [...loadLog];
                            updated[index].current_a = e.target.value;
                            setLoadLog(updated);
                          }} placeholder="A" className="w-16 px-2 py-1 border rounded text-sm" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" step="0.01" value={entry.pf} onChange={(e) => {
                            const updated = [...loadLog];
                            updated[index].pf = e.target.value;
                            setLoadLog(updated);
                          }} placeholder="PF" className="w-14 px-2 py-1 border rounded text-sm" />
                        </td>
                        <td className="px-3 py-2">
                          <select value={entry.operating_state} onChange={(e) => {
                            const updated = [...loadLog];
                            updated[index].operating_state = e.target.value;
                            setLoadLog(updated);
                          }} className="w-20 px-2 py-1 border rounded text-sm">
                            {OPERATING_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <button onClick={() => setLoadLog(loadLog.filter((_, i) => i !== index))} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
  
  // ==================== SEU SELECTOR COMPONENT ====================
  const SEUSelector = ({ filterType, color, icon: Icon }) => {
    // Filter linked SEUs by type
    const filteredSEUs = linkedSEUs.filter(seu => {
      if (filterType === 'electricity') return seu.seu_type === 'electricity';
      if (filterType === 'fuel') return seu.seu_type === 'fuel';
      if (filterType === 'water') return seu.seu_type === 'water';
      if (filterType === 'waste') return seu.seu_type === 'waste';
      if (filterType === 'water-waste') return seu.seu_type === 'water' || seu.seu_type === 'waste';
      return true;
    });
    
    if (filteredSEUs.length === 0) {
      return (
        <div className={`bg-${color}-50 border border-${color}-200 rounded-xl p-6 text-center`}>
          <Icon className={`w-10 h-10 text-${color}-400 mx-auto mb-2`} />
          <h3 className="text-md font-semibold text-gray-700 mb-2">No {filterType === 'water-waste' ? 'Water/Waste' : filterType.charAt(0).toUpperCase() + filterType.slice(1)} SEUs Linked</h3>
          <p className="text-sm text-gray-500 mb-3">Go to SEU Selection and link the relevant SEUs first.</p>
          <button onClick={() => setActivePhase('seu-selection')} className={`px-4 py-2 bg-${color}-500 text-white rounded-lg font-medium hover:bg-${color}-600 text-sm`}>
            Go to SEU Selection
          </button>
        </div>
      );
    }
    
    return (
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select SEU to Measure</label>
        <div className="flex flex-wrap gap-2">
          {filteredSEUs.map(seu => (
            <button
              key={seu.seu_id}
              onClick={() => setSelectedSEU(seu)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                selectedSEU?.seu_id === seu.seu_id 
                  ? `bg-${color}-500 text-white shadow-md` 
                  : `bg-gray-100 text-gray-700 hover:bg-${color}-100`
              }`}
              style={{
                backgroundColor: selectedSEU?.seu_id === seu.seu_id ? 
                  (color === 'yellow' ? '#eab308' : color === 'orange' ? '#f97316' : color === 'cyan' ? '#06b6d4' : '#3b82f6') : undefined,
                color: selectedSEU?.seu_id === seu.seu_id ? 'white' : undefined
              }}
            >
              <Icon className="w-4 h-4" />
              {seu.seu_code}
            </button>
          ))}
        </div>
        {selectedSEU && filteredSEUs.some(s => s.seu_id === selectedSEU.seu_id) && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">Desktop Data: {selectedSEU.desktop_data?.equipment_type} • {selectedSEU.desktop_data?.final_annual_energy?.toLocaleString() || '-'} {filterType === 'electricity' ? 'kWh/yr' : filterType === 'fuel' ? 'GJ/yr' : 'units/yr'}</p>
          </div>
        )}
      </div>
    );
  };
  
  // ==================== THERMAL PHASE ====================
  const renderThermalPhase = () => (
    <div className="space-y-6">
      {/* SEU Selector */}
      <SEUSelector filterType="fuel" color="orange" icon={Flame} />
      
      {!selectedSEU || selectedSEU.seu_type !== 'fuel' ? (
        linkedSEUs.filter(s => s.seu_type === 'fuel').length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
            <AlertTriangle className="w-10 h-10 text-yellow-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Select a fuel SEU from above to enter measurements</p>
          </div>
        )
      ) : (
        <>
          {/* Selected SEU Header */}
          <div className="bg-gradient-to-r from-orange-400 to-red-400 rounded-xl p-4 text-white">
            <div className="flex items-center gap-3">
              <Flame className="w-8 h-8" />
              <div>
                <p className="font-bold text-lg">{selectedSEU.seu_code}</p>
                <p className="text-orange-100">{selectedSEU.desktop_data?.equipment_type} • {selectedSEU.desktop_data?.fuel_type}</p>
              </div>
            </div>
          </div>
          
          {/* Combustion Data */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Thermometer className="w-5 h-5 text-red-600" />
                Combustion & Performance
              </h3>
              <button onClick={handleSaveThermalCombustion} disabled={saving} className="px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 text-sm">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'stack_temp_c', label: 'Stack Temp', unit: '°C', placeholder: '215' },
                { key: 'ambient_temp_c', label: 'Ambient', unit: '°C', placeholder: '32' },
                { key: 'o2_percent', label: 'O₂', unit: '%', placeholder: '7.8' },
                { key: 'co_ppm', label: 'CO', unit: 'ppm', placeholder: '320' },
                { key: 'excess_air_percent', label: 'Excess Air', unit: '%', placeholder: '42' },
                { key: 'flue_gas_loss_percent', label: 'Flue Loss', unit: '%', placeholder: '18' },
                { key: 'estimated_efficiency_percent', label: 'Efficiency', unit: '%', placeholder: '72' },
                { key: 'fuel_flow_rate', label: 'Fuel Flow', unit: '', placeholder: '120' }
              ].map(field => (
                <div key={field.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{field.label} {field.unit && <span className="text-gray-400">({field.unit})</span>}</label>
                  <input type="number" step="0.1" value={thermalData[field.key]} onChange={(e) => setThermalData({...thermalData, [field.key]: e.target.value})} placeholder={field.placeholder} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
              ))}
            </div>
          </div>
          
          {/* Steam Observations */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Steam / Distribution
              </h3>
              <button onClick={handleSaveSteam} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 text-sm">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Traps Checked</label>
                <input type="number" value={steamData.steam_traps_checked} onChange={(e) => setSteamData({...steamData, steam_traps_checked: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Traps Leaking</label>
                <input type="number" value={steamData.steam_traps_leaking} onChange={(e) => setSteamData({...steamData, steam_traps_leaking: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Condensate Recovery</label>
                <select value={steamData.condensate_recovery} onChange={(e) => setSteamData({...steamData, condensate_recovery: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
                  <option value="">Select...</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="Partial">Partial</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Insulation</label>
                <select value={steamData.insulation_condition} onChange={(e) => setSteamData({...steamData, insulation_condition: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
                  <option value="">Select...</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                </select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" checked={steamData.visible_leaks} onChange={(e) => setSteamData({...steamData, visible_leaks: e.target.checked})} className="w-4 h-4" />
                <label className="text-sm text-gray-700">Visible Steam Leaks</label>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
  
  // ==================== WATER/WASTE PHASE ====================
  const renderWaterWastePhase = () => (
    <div className="space-y-6">
      {/* SEU Selector for Water and Waste */}
      <SEUSelector filterType="water-waste" color="cyan" icon={Droplets} />
      
      {!selectedSEU || (selectedSEU.seu_type !== 'water' && selectedSEU.seu_type !== 'waste') ? (
        linkedSEUs.filter(s => s.seu_type === 'water' || s.seu_type === 'waste').length > 0 && (
          <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-6 text-center">
            <Droplets className="w-10 h-10 text-cyan-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Select a water or waste SEU from above to enter observations</p>
          </div>
        )
      ) : (
        <>
          {/* Header */}
          <div className={`rounded-xl p-4 text-white ${selectedSEU.seu_type === 'water' ? 'bg-gradient-to-r from-cyan-400 to-blue-400' : 'bg-gradient-to-r from-green-400 to-teal-400'}`}>
            <div className="flex items-center gap-3">
              {selectedSEU.seu_type === 'water' ? <Droplets className="w-8 h-8" /> : <Trash className="w-8 h-8" />}
              <div>
                <p className="font-bold text-lg">{selectedSEU.seu_code}</p>
                <p className="opacity-80">{selectedSEU.desktop_data?.equipment_type}</p>
              </div>
            </div>
          </div>
          
          {/* Water Observations */}
          {(selectedSEU.seu_type === 'water' || selectedSEU.seu_type === 'electricity') && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-cyan-600" />
                  Water Observations
                </h3>
                <button onClick={handleSaveWater} disabled={saving} className="px-4 py-2 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 disabled:opacity-50 text-sm">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Cooling Tower CoC</label>
                  <input type="number" step="0.1" value={waterData.cooling_tower_coc} onChange={(e) => setWaterData({...waterData, cooling_tower_coc: e.target.value})} placeholder="2.8" className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Daily Consumption (kL)</label>
                  <input type="number" value={waterData.daily_consumption_kl} onChange={(e) => setWaterData({...waterData, daily_consumption_kl: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
                  <input type="text" value={waterData.remarks} onChange={(e) => setWaterData({...waterData, remarks: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
              </div>
              
              <div className="mt-4 flex flex-wrap gap-4">
                {[
                  { key: 'visible_leaks', label: 'Visible Leaks' },
                  { key: 'excess_blowdown', label: 'Excess Blowdown' },
                  { key: 'reuse_possible', label: 'Reuse Possible' },
                  { key: 'ro_reject_to_drain', label: 'RO Reject to Drain' }
                ].map(item => (
                  <label key={item.key} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={waterData[item.key]} onChange={(e) => setWaterData({...waterData, [item.key]: e.target.checked})} className="w-4 h-4" />
                    {item.label}
                  </label>
                ))}
              </div>
            </div>
          )}
          
          {/* Waste Observations */}
          {(selectedSEU.seu_type === 'waste' || selectedSEU.seu_type === 'electricity') && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Trash className="w-5 h-5 text-green-600" />
                  Waste Observations
                </h3>
                <button onClick={handleSaveWaste} disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 text-sm">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Waste Stream</label>
                  <input type="text" value={wasteData.waste_stream} onChange={(e) => setWasteData({...wasteData, waste_stream: e.target.value})} placeholder="ETP Sludge" className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Handling Method</label>
                  <select value={wasteData.handling_method} onChange={(e) => setWasteData({...wasteData, handling_method: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="">Select...</option>
                    <option value="Landfill">Landfill</option>
                    <option value="Incineration">Incineration</option>
                    <option value="Recycling">Recycling</option>
                    <option value="Composting">Composting</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Moisture Content</label>
                  <select value={wasteData.moisture_content} onChange={(e) => setWasteData({...wasteData, moisture_content: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="">Select...</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Monthly Qty</label>
                  <input type="number" value={wasteData.monthly_qty} onChange={(e) => setWasteData({...wasteData, monthly_qty: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
                  <select value={wasteData.unit} onChange={(e) => setWasteData({...wasteData, unit: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="MT">MT</option>
                    <option value="kg">kg</option>
                    <option value="L">Liters</option>
                  </select>
                </div>
              </div>
              
              <div className="mt-4 flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={wasteData.reduction_possible} onChange={(e) => setWasteData({...wasteData, reduction_possible: e.target.checked})} className="w-4 h-4" />
                  Reduction Possible
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={wasteData.segregation_in_place} onChange={(e) => setWasteData({...wasteData, segregation_in_place: e.target.checked})} className="w-4 h-4" />
                  Segregation in Place
                </label>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
  
  // ==================== OBSERVATIONS PHASE ====================
  const renderObservationsPhase = () => (
    <div className="space-y-6">
      {/* SEU Selector - All types */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select SEU for Observation</label>
        {linkedSEUs.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-2">No SEUs linked to this session yet.</p>
            <button onClick={() => setActivePhase('seu-selection')} className="px-4 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 text-sm">
              Go to SEU Selection
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {linkedSEUs.map(seu => (
              <button
                key={seu.seu_id}
                onClick={() => setSelectedSEU(seu)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  selectedSEU?.seu_id === seu.seu_id 
                    ? 'bg-purple-500 text-white shadow-md' 
                    : 'bg-gray-100 text-gray-700 hover:bg-purple-100'
                }`}
              >
                {seu.seu_type === 'electricity' && <Zap className="w-4 h-4" />}
                {seu.seu_type === 'fuel' && <Flame className="w-4 h-4" />}
                {seu.seu_type === 'water' && <Droplets className="w-4 h-4" />}
                {seu.seu_type === 'waste' && <Trash className="w-4 h-4" />}
                {seu.seu_code}
              </button>
            ))}
          </div>
        )}
        {selectedSEU && (
          <div className="mt-3 p-3 bg-purple-50 rounded-lg">
            <p className="text-xs text-purple-700">Recording observations for: <strong>{selectedSEU.seu_code}</strong> ({selectedSEU.desktop_data?.equipment_type})</p>
          </div>
        )}
      </div>
      
      {/* Add Observation Form */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Eye className="w-5 h-5 text-purple-600" />
          Add Equipment Observation
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Issue Category *</label>
            <select value={newObservation.observation_type} onChange={(e) => setNewObservation({...newObservation, observation_type: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
              <option value="">Select...</option>
              <option value="Oversized motor">Oversized motor</option>
              <option value="Throttling control">Throttling control</option>
              <option value="Excess vibration">Excess vibration</option>
              <option value="Overheating">Overheating</option>
              <option value="Air leak">Air leak</option>
              <option value="Steam leak">Steam leak</option>
              <option value="Poor maintenance">Poor maintenance</option>
              <option value="Inefficient operation">Inefficient operation</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Probable Cause</label>
            <input type="text" value={newObservation.probable_cause} onChange={(e) => setNewObservation({...newObservation, probable_cause: e.target.value})} placeholder="e.g., Oversizing" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Description *</label>
            <input type="text" value={newObservation.description} onChange={(e) => setNewObservation({...newObservation, description: e.target.value})} placeholder="e.g., Motor running at ~40% load" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Severity</label>
            <select value={newObservation.severity} onChange={(e) => setNewObservation({...newObservation, severity: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Efficiency Impact</label>
            <select value={newObservation.efficiency_impact} onChange={(e) => setNewObservation({...newObservation, efficiency_impact: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>
        </div>
        
        <div className="mt-4 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={newObservation.safety_risk} onChange={(e) => setNewObservation({...newObservation, safety_risk: e.target.checked})} className="w-4 h-4" />
            Safety Risk
          </label>
          <button onClick={handleAddObservation} disabled={saving || !selectedSEU} className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Observation
          </button>
        </div>
      </div>
      
      {/* Observations List */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="font-bold text-gray-900 mb-4">Recorded Observations ({observations.length})</h3>
        
        {observations.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No observations recorded yet.</p>
        ) : (
          <div className="space-y-3">
            {observations.map(obs => (
              <div key={obs.id} className="p-4 bg-gray-50 rounded-lg flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      obs.severity === 'High' ? 'bg-red-100 text-red-700' :
                      obs.severity === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>{obs.severity}</span>
                    <span className="font-medium text-gray-900">{obs.observation_type}</span>
                  </div>
                  <p className="text-sm text-gray-600">{obs.description}</p>
                  {obs.probable_cause && <p className="text-xs text-gray-500 mt-1">Cause: {obs.probable_cause}</p>}
                </div>
                <button onClick={() => handleDeleteObservation(obs.id)} className="text-red-500 hover:text-red-700 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
  
  // ==================== VERIFICATION PHASE ====================
  const renderVerificationPhase = () => (
    <div className="space-y-6">
      {/* Electrical Verification */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-600" />
            Electrical Verification
          </h3>
          <button onClick={() => handleSaveVerification('electrical')} disabled={saving || !selectedSEU} className="px-4 py-2 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 disabled:opacity-50 text-sm">
            Save
          </button>
        </div>
        
        <div className="space-y-2">
          {electricalVerification.map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded">
              <input type="checkbox" checked={item.checked} onChange={(e) => {
                const updated = [...electricalVerification];
                updated[idx].checked = e.target.checked;
                setElectricalVerification(updated);
              }} className="w-5 h-5" />
              <span className="flex-1 text-sm">{item.item}</span>
              <input type="text" value={item.remarks} onChange={(e) => {
                const updated = [...electricalVerification];
                updated[idx].remarks = e.target.value;
                setElectricalVerification(updated);
              }} placeholder="Remarks" className="w-32 px-2 py-1 text-sm border rounded" />
            </div>
          ))}
        </div>
      </div>
      
      {/* Thermal Verification */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-600" />
            Thermal Verification
          </h3>
          <button onClick={() => handleSaveVerification('thermal')} disabled={saving || !selectedSEU} className="px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 text-sm">
            Save
          </button>
        </div>
        
        <div className="space-y-2">
          {thermalVerification.map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded">
              <input type="checkbox" checked={item.checked} onChange={(e) => {
                const updated = [...thermalVerification];
                updated[idx].checked = e.target.checked;
                setThermalVerification(updated);
              }} className="w-5 h-5" />
              <span className="flex-1 text-sm">{item.item}</span>
              <input type="text" value={item.remarks} onChange={(e) => {
                const updated = [...thermalVerification];
                updated[idx].remarks = e.target.value;
                setThermalVerification(updated);
              }} placeholder="Remarks" className="w-32 px-2 py-1 text-sm border rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  
  // ==================== INSIGHTS PHASE ====================
  const renderInsightsPhase = () => {
    // Helper function to format AI narrative with proper styling
    const renderNarrativeSections = (narrativeStr) => {
      if (!narrativeStr) return null;

      let data;
      try {
        data = typeof narrativeStr === 'string' ? JSON.parse(narrativeStr) : narrativeStr;
      } catch {
        // Not JSON — render as plain text (legacy fallback)
        return <pre className="whitespace-pre-wrap text-gray-700 text-sm leading-relaxed">{narrativeStr}</pre>;
      }

      // Fallback wrapper from backend
      if (data.raw_text) {
        return <pre className="whitespace-pre-wrap text-gray-700 text-sm leading-relaxed">{data.raw_text}</pre>;
      }

      // Structured JSON rendering
      return (
        <div className="space-y-5">
          {/* Executive Summary */}
          {data.executive_summary && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-5">
              <h3 className="text-lg font-bold text-indigo-700 mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5" /> Executive Summary
              </h3>
              <p className="text-gray-700 leading-relaxed">{data.executive_summary}</p>
            </div>
          )}

          {/* Sections */}
          {data.sections?.map((section, sIdx) => (
            <div key={sIdx} className="bg-white border rounded-lg shadow-sm overflow-hidden">
              <div className="bg-indigo-600 px-5 py-3">
                <h3 className="text-white font-bold">{section.title}</h3>
              </div>
              <div className="p-5 space-y-4">
                {section.content && <p className="text-gray-700 leading-relaxed">{section.content}</p>}
                {section.subsections?.map((sub, subIdx) => (
                  <div key={subIdx} className="border-l-4 border-indigo-200 pl-4">
                    <h4 className="text-base font-semibold text-gray-800 mb-2">{sub.title}</h4>
                    {sub.content && <p className="text-gray-600 text-sm mb-2 leading-relaxed">{sub.content}</p>}
                    {sub.findings?.length > 0 && (
                      <div className="mb-2">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Findings</span>
                        <ul className="list-disc pl-5 mt-1 space-y-1">
                          {sub.findings.map((f, i) => <li key={i} className="text-sm text-gray-700">{f}</li>)}
                        </ul>
                      </div>
                    )}
                    {sub.recommendations?.length > 0 && (
                      <div>
                        <span className="text-xs font-semibold text-green-600 uppercase tracking-wide">Recommendations</span>
                        <ul className="list-disc pl-5 mt-1 space-y-1">
                          {sub.recommendations.map((r, i) => <li key={i} className="text-sm text-green-700">{r}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Priority Actions */}
          {data.priority_actions && (
            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
              <div className="bg-indigo-600 px-5 py-3">
                <h3 className="text-white font-bold">Priority Action Items</h3>
              </div>
              <div className="p-5 space-y-4">
                {[
                  { key: 'immediate', label: 'Immediate Actions (No Cost)', color: 'green' },
                  { key: 'short_term', label: 'Short-term Improvements', color: 'yellow' },
                  { key: 'medium_term', label: 'Medium-term Projects', color: 'orange' }
                ].map(({ key, label, color }) => {
                  const items = data.priority_actions[key];
                  if (!items?.length) return null;
                  const bgMap = { green: 'bg-green-50 border-green-300', yellow: 'bg-yellow-50 border-yellow-300', orange: 'bg-orange-50 border-orange-300' };
                  const textMap = { green: 'text-green-700', yellow: 'text-yellow-700', orange: 'text-orange-700' };
                  return (
                    <div key={key}>
                      <h4 className={`text-sm font-bold ${textMap[color]} mb-2`}>{label}</h4>
                      <div className="space-y-2">
                        {items.map((item, i) => (
                          <div key={i} className={`${bgMap[color]} border rounded-lg p-3`}>
                            <p className="text-sm font-medium text-gray-800">{item.action}</p>
                            <p className="text-xs text-gray-600 mt-1">Impact: {item.impact}</p>
                            {item.investment && <p className="text-xs text-gray-500">Investment: {item.investment}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Estimated Savings */}
          {data.estimated_savings && (
            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
              <div className="bg-indigo-600 px-5 py-3">
                <h3 className="text-white font-bold">Estimated Savings Potential</h3>
              </div>
              <div className="p-5">
                {data.estimated_savings.summary && (
                  <p className="text-gray-700 mb-4 leading-relaxed">{data.estimated_savings.summary}</p>
                )}
                {data.estimated_savings.items?.length > 0 && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 text-gray-600 font-semibold">Area</th>
                        <th className="text-right py-2 text-gray-600 font-semibold">Savings</th>
                        <th className="text-right py-2 text-gray-600 font-semibold">Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.estimated_savings.items.map((item, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-2 text-gray-800">{item.area}</td>
                          <td className="py-2 text-right font-medium text-gray-900">{item.savings}</td>
                          <td className="py-2 text-right text-gray-500">{item.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      );
    };
    
    return (
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button 
            onClick={() => activeSession && fetchInsights(activeSession.id)} 
            disabled={insightsLoading} 
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2 border"
          >
            {insightsLoading ? <Clock className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
            {insightsLoading ? 'Loading...' : 'Quick Analysis'}
          </button>
          <button 
            onClick={() => activeSession && generateAiNarrative(activeSession.id)} 
            disabled={narrativeLoading} 
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {narrativeLoading ? <Clock className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />}
            {narrativeLoading ? 'Generating AI Report...' : 'Generate AI Insights'}
          </button>
        </div>
        
        {/* AI Narrative Section */}
        {aiNarrative && (
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-sm border border-indigo-200 p-6">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b border-indigo-200">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Lightbulb className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">AI-Generated Energy Audit Report</h3>
                <p className="text-xs text-gray-500">
                  Generated: {formatDateTime(aiNarrative.generated_at)} •
                  {aiNarrative.measurement_summary?.electrical_count || 0} electrical, 
                  {aiNarrative.measurement_summary?.thermal_count || 0} thermal, 
                  {aiNarrative.measurement_summary?.observations_count || 0} observations analyzed
                </p>
              </div>
            </div>
            <div className="prose prose-sm max-w-none">
              {renderNarrativeSections(aiNarrative.narrative)}
            </div>
          </div>
        )}
        
        {/* Rule-based Insights Summary */}
        {insights && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm border text-center">
                <p className="text-3xl font-bold text-gray-900">{insights.total_insights}</p>
                <p className="text-xs text-gray-500">Total Insights</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 border border-red-200 text-center">
                <p className="text-3xl font-bold text-red-600">{insights.critical}</p>
                <p className="text-xs text-red-600">Critical</p>
              </div>
              <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200 text-center">
                <p className="text-3xl font-bold text-yellow-600">{insights.warnings}</p>
                <p className="text-xs text-yellow-600">Warnings</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 border border-green-200 text-center">
                <p className="text-3xl font-bold text-green-600">{insights.opportunities}</p>
                <p className="text-xs text-green-600">Opportunities</p>
              </div>
            </div>
            
            {/* Insights List */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="font-bold text-gray-900 mb-4">Rule-Based Insights</h3>
              
              {insights.insights.length === 0 ? (
                <p className="text-gray-500 text-center py-6">No rule-based insights detected. Add more measurements to see automatic recommendations.</p>
              ) : (
                <div className="space-y-3">
                  {insights.insights.map((insight, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border-l-4 ${
                      insight.type === 'critical' ? 'bg-red-50 border-red-500' :
                      insight.type === 'warning' ? 'bg-yellow-50 border-yellow-500' :
                      insight.type === 'opportunity' ? 'bg-green-50 border-green-500' :
                      'bg-gray-50 border-gray-400'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              insight.type === 'critical' ? 'bg-red-100 text-red-700' :
                              insight.type === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>{insight.category}</span>
                            <span className="text-xs text-gray-500">{insight.seu_code}</span>
                          </div>
                          <p className="font-medium text-gray-900">{insight.trigger}</p>
                          <p className="text-sm text-gray-600 mt-1">{insight.recommendation}</p>
                          <p className="text-xs text-gray-500 mt-1">Potential: {insight.potential_savings}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        
        {/* Empty State */}
        {!insights && !aiNarrative && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-8 text-center">
            <Lightbulb className="w-12 h-12 text-indigo-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">AI-Powered Energy Insights</h3>
            <p className="text-gray-500 mb-4">
              Click "Generate AI Insights" to get a comprehensive analysis of your on-site measurements,<br/>
              or "Quick Analysis" for rule-based recommendations.
            </p>
          </div>
        )}
      </div>
    );
  };
  
  // ==================== SUMMARY PHASE ====================
  const renderSummaryPhase = () => (
    <div className="space-y-6">
      {!sessionSummary ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">Loading summary...</p>
        </div>
      ) : (
        <>
          {/* Session Info */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="font-bold text-gray-900 mb-4">Session Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{sessionSummary.total_seus_linked}</p>
                <p className="text-xs text-gray-500">SEUs Linked</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{sessionSummary.total_seus_measured}</p>
                <p className="text-xs text-gray-500">SEUs Measured</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{sessionSummary.observations_count}</p>
                <p className="text-xs text-gray-500">Observations</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">{sessionSummary.evidence_count}</p>
                <p className="text-xs text-gray-500">Evidence Files</p>
              </div>
            </div>
          </div>
          
          {/* Variance Analysis */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="font-bold text-gray-900 mb-4">Variance Analysis (Desktop vs Measured)</h3>
            
            {sessionSummary.summary_items.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No measurements recorded yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">SEU</th>
                      <th className="px-3 py-2 text-left">Equipment</th>
                      <th className="px-3 py-2 text-right">Desktop</th>
                      <th className="px-3 py-2 text-right">Measured</th>
                      <th className="px-3 py-2 text-center">Variance</th>
                      <th className="px-3 py-2 text-center">Confidence</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sessionSummary.summary_items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2 font-medium">{item.seu_code}</td>
                        <td className="px-3 py-2 text-gray-600">{item.equipment}</td>
                        <td className="px-3 py-2 text-right">{item.desktop_value?.toLocaleString()} {item.desktop_unit}</td>
                        <td className="px-3 py-2 text-right">{item.measured_value?.toLocaleString() || '-'} {item.measured_unit}</td>
                        <td className="px-3 py-2 text-center">
                          {item.variance_percent !== 0 && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              Math.abs(item.variance_percent) > 15 ? 'bg-red-100 text-red-700' :
                              Math.abs(item.variance_percent) > 5 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {item.variance_percent > 0 ? '+' : ''}{item.variance_percent}%
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className="text-xs">{item.desktop_confidence} → <span className="font-medium text-green-600">{item.new_confidence}</span></span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Close Session */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900">Close Session</h3>
                <p className="text-sm text-gray-600">Lock this session and push data to Opportunity Engine</p>
              </div>
              <button onClick={handleCloseSession} disabled={saving || sessionSummary.session?.status === 'Closed'} className="px-6 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-900 disabled:opacity-50 flex items-center gap-2">
                <Lock className="w-5 h-5" />
                {sessionSummary.session?.status === 'Closed' ? 'Session Closed' : 'Save & Lock Session'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
  
  // ==================== RENDER ====================
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  const isPhaseUnlocked = (phaseId) => {
    if (phaseId === 'safety') return true;
    return safetyCompleted;
  };
  
  const isPhaseCompleted = (phaseId) => {
    return completedPhases.includes(phaseId);
  };
  
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/ers/study/${studyId}?tab=onsite-study`)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">On-Site Measurement</h1>
                <p className="text-xs text-gray-500">{study?.study_name}</p>
              </div>
            </div>
            
            {activeSession && (
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  safetyCompleted ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {activeSession.session_id}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Phase Navigation */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between overflow-x-auto pb-2">
            {PHASES.map((phase, index) => (
              <PhaseIcon
                key={phase.id}
                phase={phase}
                index={index}
                isActive={activePhase === phase.id}
                isUnlocked={isPhaseUnlocked(phase.id)}
                isCompleted={isPhaseCompleted(phase.id)}
                onClick={setActivePhase}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* Phase Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Phase Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {PHASES.find(p => p.id === activePhase)?.label}
          </h2>
          <p className="text-gray-500">
            {PHASES.find(p => p.id === activePhase)?.description}
          </p>
        </div>
        
        {/* Render Active Phase */}
        {activePhase === 'safety' && renderSafetyPhase()}
        {activePhase === 'seu-selection' && renderSEUSelectionPhase()}
        {activePhase === 'electrical' && renderElectricalPhase()}
        {activePhase === 'thermal' && renderThermalPhase()}
        {activePhase === 'water-waste' && renderWaterWastePhase()}
        {activePhase === 'observations' && renderObservationsPhase()}
        {activePhase === 'verification' && renderVerificationPhase()}
        {activePhase === 'insights' && renderInsightsPhase()}
        {activePhase === 'summary' && renderSummaryPhase()}
      </div>
      
      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-red-50 p-4 border-b border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Delete Measurement Session?</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete session <strong>{deleteConfirm.session?.session_id}</strong>?
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This will permanently delete:
                </p>
                <ul className="text-sm text-yellow-700 mt-2 space-y-1 list-disc list-inside">
                  <li>All electrical measurements</li>
                  <li>All thermal/combustion data</li>
                  <li>All water & waste observations</li>
                  <li>All field observations</li>
                  <li>Verification checklists</li>
                  <li>AI-generated insights</li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm({ show: false, session: null })}
                  disabled={deleting}
                  className="flex-1 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteSession}
                  disabled={deleting}
                  className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <Clock className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Session
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
